import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    VStack,
    HStack,
    Card,
    CardBody,
    CardHeader,
    Heading,
    Text,
    Button,
    useColorModeValue,
    Spinner,
    Alert,
    AlertIcon,
    AlertDescription,
    Badge,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Flex,
    IconButton,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    useToast,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Select,
    Input,
    InputGroup,
    InputLeftElement,
    ButtonGroup,
    Tooltip,
    Grid,
    GridItem,
    Divider,
    Switch,
    FormControl,
    FormLabel
} from '@chakra-ui/react';
import {
    MdSearch,
    MdFilterList,
    MdRefresh,
    MdAssignment,
    MdCheckCircle,
    MdCancel,
    MdDelete,
    MdBusiness,
    MdDescription,
    MdSchedule,
    MdMessage,
    MdClose,
    MdFullscreen,
    MdFullscreenExit,
    MdVisibilityOff,
    MdVisibility,
    MdPause,
    MdPlayArrow,
    MdCode,              // ADD THIS
    MdContentCopy   
} from 'react-icons/md';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import apiService from '../services/apiService';
import { formatDateTime, formatNumber } from '../utils/helpers';
import PaginationControls from '../components/Common/PaginationControls';
import { useSearchParams } from 'react-router-dom';

/**
 * Issues Management Page Component
 */
function Issues({ userProfile, appConfig, authServiceReady }) {
    // Theme-aware styling 
    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const textColor = useColorModeValue('gray.600', 'gray.300');
    const headingColor = useColorModeValue('gray.800', 'white');
    const hoverBg = useColorModeValue('gray.50', 'gray.700');
    const fullscreenBg = useColorModeValue('gray.50', 'gray.900');
    
    // Additional theme colors for filter persistence notice
    const persistenceNoticeBg = useColorModeValue('blue.50', 'blue.900');
    const persistenceNoticeTextPrimary = useColorModeValue('blue.700', 'blue.200');
    const persistenceNoticeTextSecondary = useColorModeValue('blue.600', 'blue.300');

    // Check admin role from localStorage
    const isAdmin = localStorage.getItem('userRole') === 'admin';
    const [searchParams] = useSearchParams();

    // Tab configuration with issue ID column removed and dynamic action column based on user role
    const tabs = [
        {
            key: 'issues',
            label: 'Issues',
            count: 0,
            defaultFilters: { 
                is_issue_closed: false, 
                issue_taken_by: 'null' 
            },
            columns: ['client', 'report', 'message', 'status', 'created', 'actions']
        },
        {
            key: 'taken',
            label: 'Issues Assigned',
            count: 0,
            defaultFilters: { 
                is_issue_closed: false, 
                issue_taken_by: 'notNull' 
            },
            columns: ['client', 'report', 'message', 'status', 'issueTakenBy', 'created', 'actions']
        },
        {
            key: 'fixed',
            label: 'Issues Fixed',
            count: 0,
            defaultFilters: { 
                is_issue_closed: true, 
                fixed_by: 'notNull' 
            },
            columns: isAdmin 
                ? ['client', 'report', 'message', 'status', 'issueTakenBy', 'fixedBy', 'created', 'actions']
                : ['client', 'report', 'message', 'status', 'issueTakenBy', 'fixedBy', 'created']
        },
        {
            key: 'notIssue',
            label: 'Not an Issues',
            count: 0,
            defaultFilters: { 
                is_issue_closed: true, 
                not_an_issue_by: 'notNull' 
            },
            columns: isAdmin 
                ? ['client', 'report', 'message', 'status', 'issueTakenBy', 'notAnIssueBy', 'created', 'actions']
                : ['client', 'report', 'message', 'status', 'issueTakenBy', 'notAnIssueBy', 'created']
        }
    ];

    // State management
    const [activeTab, setActiveTab] = useState(0);
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tabCounts, setTabCounts] = useState([0, 0, 0, 0]);
    const [pagination, setPagination] = useState({
        page: 1,
        size: 20,
        total: 0,
        totalPages: 0
    });

    // New state for fullscreen mode and controls with auto refresh enabled by default
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showFilters, setShowFilters] = useState(true);
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true); 

    // Filter state with backend parameter names - keep persistent across tab switches
    const [userFilters, setUserFilters] = useState({
    client_id: '',
    report_code: '',
    search: '',
    startDate: null,
    endDate: null
    });

    // Add new state for date range picker
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    const [clients, setClients] = useState([]);
    const [reports, setReports] = useState([]);

    // Track if URL parameters have been processed
    const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);

    // Action modal state
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [actionType, setActionType] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    // custom styles for DatePicker to match theme
    const datePickerCustomStyles = `
    .custom-datepicker-wrapper {
        display: block;
        width: 100%;
    }

    .custom-datepicker {
        width: 100%;
        height: 2rem;
        min-height: 2rem;
        padding-left: 1rem;
        padding-right: 1rem;
        font-size: 0.875rem;
        line-height: 1.25rem;
        border:1px solid #dcdcdc;
        border-radius: 0.375rem;
        border-color: ${borderColor};
        background-color: ${cardBg};
        color: ${textColor};
        transition-property: background-color, border-color, color, fill, stroke, opacity, box-shadow, transform;
        transition-duration: 200ms;
        cursor: pointer;
        outline: 2px solid transparent;
        outline-offset: 2px;
    }

    .custom-datepicker:hover {
        border-color: ${useColorModeValue('gray.300', 'whiteAlpha.400')};
    }

    .custom-datepicker:focus {
        z-index: 1;
        border-color: var(--chakra-colors-brand-500);
        box-shadow: 0 0 0 1px var(--chakra-colors-brand-500);
    }

    .custom-datepicker::placeholder {
        color: ${useColorModeValue('gray.400', 'whiteAlpha.400')};
    }

    .react-datepicker-popper {
        z-index: 9999 !important;
    }

    .react-datepicker {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
        border: 1px solid ${borderColor};
        border-radius: 0.5rem;
        background-color: ${cardBg};
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    }

    .react-datepicker__header {
        background-color: ${hoverBg};
        border-bottom: 1px solid ${borderColor};
        border-radius: 0.5rem 0.5rem 0 0;
        padding-top: 0.75rem;
    }

    .react-datepicker__current-month {
        color: ${headingColor};
        font-weight: 600;
        font-size: 0.95rem;
        margin-bottom: 0.5rem;
    }

    .react-datepicker__day-name {
        color: ${textColor};
        font-weight: 500;
        font-size: 0.85rem;
        width: 2rem;
        line-height: 2rem;
    }

    .react-datepicker__day {
        color: ${textColor};
        width: 2rem;
        line-height: 2rem;
        font-size: 0.85rem;
        margin: 0.15rem;
        border-radius: 0.375rem;
    }

    .react-datepicker__day:hover {
        background-color: ${hoverBg};
        border-radius: 0.375rem;
    }

    .react-datepicker__day--selected,
    .react-datepicker__day--in-selecting-range,
    .react-datepicker__day--in-range {
        background-color: var(--chakra-colors-brand-500) !important;
        color: white !important;
        font-weight: 500;
        border-radius: 0.375rem;
    }

    .react-datepicker__day--range-start {
        border-radius: 0.375rem !important;
    }

    .react-datepicker__day--range-end {
        border-radius: 0.375rem !important;
    }

    .react-datepicker__day--keyboard-selected {
        background-color: var(--chakra-colors-brand-300);
        color: white;
        border-radius: 0.375rem;
    }

    .react-datepicker__day--disabled {
        color: var(--chakra-colors-gray-400);
        cursor: not-allowed;
    }

    .react-datepicker__day--disabled:hover {
        background-color: transparent;
    }

    .react-datepicker__day--outside-month {
        color: var(--chakra-colors-gray-400);
    }

    .react-datepicker__navigation {
        top: 0.75rem;
    }

    .react-datepicker__navigation-icon::before {
        border-color: ${textColor};
        border-width: 2px 2px 0 0;
    }

    .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before {
        border-color: var(--chakra-colors-brand-500);
    }

    .react-datepicker__month {
        margin: 0.75rem;
    }

    .react-datepicker__day-names {
        margin-bottom: 0.25rem;
    }

    .react-datepicker__week {
        display: flex;
    }

    .react-datepicker__triangle {
        display: none;
    }

    .react-datepicker__month-dropdown,
    .react-datepicker__year-dropdown {
        background-color: ${cardBg};
        border: 1px solid ${borderColor};
        border-radius: 0.375rem;
        padding: 0.25rem;
    }

    .react-datepicker__month-option,
    .react-datepicker__year-option {
        color: ${textColor};
        padding: 0.25rem 0.5rem;
        border-radius: 0.25rem;
    }

    .react-datepicker__month-option:hover,
    .react-datepicker__year-option:hover {
        background-color: ${hoverBg};
    }

    .react-datepicker__month-option--selected,
    .react-datepicker__year-option--selected {
        background-color: var(--chakra-colors-brand-500);
        color: white;
    }

    .react-datepicker__close-icon {
        right: 0.5rem;
    }

    .react-datepicker__close-icon::after {
        background-color: var(--chakra-colors-gray-400);
        font-size: 1rem;
        width: 1rem;
        height: 1rem;
        line-height: 1rem;
    }

    .react-datepicker__close-icon:hover::after {
        background-color: var(--chakra-colors-gray-500);
    }
    `;

    // Message modal state
    const { 
        isOpen: isMessageOpen, 
        onOpen: onMessageOpen, 
        onClose: onMessageClose 
    } = useDisclosure();
    const [messageToShow, setMessageToShow] = useState('');

    // Data Query modal state
    const { 
        isOpen: isDataQueryOpen, 
        onOpen: onDataQueryOpen, 
        onClose: onDataQueryClose 
    } = useDisclosure();
    const [dataQueryToShow, setDataQueryToShow] = useState('');

    // Modified auto-refresh timer with manual control
    const refreshInterval = useRef(null);
    const toast = useToast();

    /**
     * Toggle fullscreen mode
     */
    const toggleFullscreen = useCallback(() => {
        setIsFullscreen(prev => !prev);
    }, []);

    /**
     * Toggle filter panel visibility
     */
    const toggleFilters = useCallback(() => {
        setShowFilters(prev => !prev);
    }, []);

    /**
     * Toggle auto refresh
     */
    const toggleAutoRefresh = useCallback(() => {
        setAutoRefreshEnabled(prev => !prev);
    }, []);

    /**
     * Load filter options (clients and reports)
     */
    const loadFilterOptions = useCallback(async () => {
        try {
            const [clientsResponse, reportsResponse] = await Promise.all([
                apiService.getActiveClients(),
                apiService.getReports({ size: 1000 })
            ]);

            if (clientsResponse.success) {
                setClients(clientsResponse.data || []);
            }

            if (reportsResponse.success) {
                setReports(reportsResponse.data?.content || []);
            }
        } catch (error) {
            console.error('Failed to load filter options:', error);
        }
    }, []);


    // Add handler for date range changes
    const handleDateRangeChange = useCallback((update) => {
        setDateRange(update);
        const [start, end] = update;
        
        // Convert dates to ISO format for API
        setUserFilters(prev => ({
            ...prev,
            startDate: start ? start.toISOString() : '',
            endDate: end ? end.toISOString() : ''
        }));
        
        // Only trigger filter change if both dates are selected or both are cleared
        if ((start && end) || (!start && !end)) {
            setPagination(prev => ({ ...prev, page: 1 }));
        }
    }, []);

    /**
     * Build complete filter parameters for API call
     */
    const buildFilterParams = useCallback((tabIndex, userFilters, pagination) => {
    const tab = tabs[tabIndex];
    const params = {
        page: pagination.page,
        size: pagination.size,
        sortBy: 'created_at_utc',
        sortDirection: 'DESC',
        ...tab.defaultFilters,
        ...(userFilters.client_id && { client_id: parseInt(userFilters.client_id) }),
        ...(userFilters.report_code && { report_code: parseInt(userFilters.report_code) }),
        ...(userFilters.search && { search: userFilters.search.trim() }),
        ...(userFilters.startDate && { startDate: userFilters.startDate }),
        ...(userFilters.endDate && { endDate: userFilters.endDate })
    };

    return params;
    }, []);

    /**
     * Load issues data for current tab with proper backend parameters
     */
    const loadIssues = useCallback(async (tabIndex = activeTab, isAutoRefresh = false) => {
        try {
            if (!isAutoRefresh) {
                setError(null);
                setLoading(true);
            }

            const params = buildFilterParams(tabIndex, userFilters, pagination);
            
            const response = await apiService.getReportTriggeredIssues(params);

            if (response.success) {
                const issuesData = response.data?.content || [];
                setIssues(issuesData);
                setPagination({
                    page: response.data?.pagination?.page || 1,
                    size: response.data?.pagination?.size || 20,
                    total: response.data?.pagination?.total || 0,
                    totalPages: response.data?.pagination?.totalPages || 0
                });
            } else {
                throw new Error(response.message || 'Failed to load issues');
            }

        } catch (error) {
            console.error('Failed to load issues:', error);
            if (!isAutoRefresh) {
                setError(error.message || 'Failed to load issues');
                toast({
                    title: 'Error',
                    description: 'Failed to load issues',
                    status: 'error',
                    duration: 5000,
                    isClosable: true
                });
            }
        } finally {
            if (!isAutoRefresh) {
                setLoading(false);
            }
        }
    }, [activeTab, pagination.page, pagination.size, userFilters, buildFilterParams, toast]);

    /**
     * Load tab counts for badges with proper backend parameters
     */
    const loadTabCounts = useCallback(async () => {
        try {
            const countPromises = tabs.map((tab, index) => {
                const params = buildFilterParams(index, userFilters, { page: 1, size: 1 });
                return apiService.getReportTriggeredIssues(params);
            });

            const responses = await Promise.all(countPromises);
            const counts = responses.map((response) => {
                return response.success ? (response.data?.pagination?.total || 0) : 0;
            });
            
            setTabCounts(counts);
        } catch (error) {
            console.error('Failed to load tab counts:', error);
        }
    }, [userFilters, buildFilterParams]);

    /**
     * Handle tab change - Keep client and report filters persistent
     */
    const handleTabChange = useCallback((index) => {
        setActiveTab(index);
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
    }, []);

    /**
     * Handle pagination change
     */
    const handlePageChange = useCallback((newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    }, []);

    /**
     * Handle page size change
     */
    const handlePageSizeChange = useCallback((newSize) => {
        setPagination(prev => ({ ...prev, page: 1, size: newSize }));
    }, []);

    /**
     * Handle filter change with backend parameter names
     */
    const handleFilterChange = useCallback((field, value) => {
        setUserFilters(prev => ({ ...prev, [field]: value }));
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
    }, []);

    /**
     * Clear all user filters - explicit manual action
     */
    const handleClearFilters = useCallback(() => {
    setUserFilters({
        client_id: '',
        report_code: '',
        search: '',
        startDate: '',
        endDate: ''
    });
    setDateRange([null, null]);
    setPagination(prev => ({ ...prev, page: 1 }));
    }, []);

    /**
     * Handle message click to show in modal
     */
    const handleMessageClick = useCallback((message) => {
        setMessageToShow(message);
        onMessageOpen();
    }, [onMessageOpen]);

    /**
     * Handle action button click
     */
    const handleAction = useCallback((issue, action) => {
        setSelectedIssue(issue);
        setActionType(action);
        onOpen();
    }, [onOpen]);

    /**
 * Handle data query click to show in modal
 */
    const handleDataQueryClick = useCallback((dataQuery) => {
        setDataQueryToShow(dataQuery);
        onDataQueryOpen();
    }, [onDataQueryOpen]);

    /**
     * Copy data query to clipboard
     */
    const handleCopyDataQuery = useCallback(() => {
        navigator.clipboard.writeText(dataQueryToShow).then(() => {
            toast({
                title: 'Copied!',
                description: 'Data query copied to clipboard',
                status: 'success',
                duration: 2000,
                isClosable: true
            });
        }).catch(() => {
            toast({
                title: 'Copy Failed',
                description: 'Failed to copy to clipboard',
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        });
    }, [dataQueryToShow, toast]);

    /**
     * Execute action with proper API calls
     */
    const executeAction = useCallback(async () => {
        if (!selectedIssue || !actionType) return;

        try {
            setActionLoading(true);
            const userEmail = localStorage.getItem('userEmail') || userProfile?.email;

            let response;
            switch (actionType) {
                case 'take':
                    response = await apiService.assignReportTriggeredIssue(selectedIssue.issueId, userEmail);
                    break;
                case 'fix':
                    response = await apiService.markReportTriggeredIssueAsFixed(selectedIssue.issueId, userEmail);
                    break;
                case 'notIssue':
                    response = await apiService.markReportTriggeredIssueAsNotAnIssue(selectedIssue.issueId, userEmail);
                    break;
                case 'delete':
                    response = await apiService.deleteReportTriggeredIssue(selectedIssue.issueId);
                    break;
                default:
                    throw new Error('Invalid action type');
            }

            if (response.success) {
                toast({
                    title: 'Success',
                    description: getActionSuccessMessage(actionType),
                    status: 'success',
                    duration: 3000,
                    isClosable: true
                });

                // Refresh data
                loadIssues();
                loadTabCounts();
            } else {
                throw new Error(response.message || 'Action failed');
            }

        } catch (error) {
            console.error('Action failed:', error);
            toast({
                title: 'Error',
                description: error.message || 'Action failed',
                status: 'error',
                duration: 5000,
                isClosable: true
            });
        } finally {
            setActionLoading(false);
            onClose();
        }
    }, [selectedIssue, actionType, userProfile, loadIssues, loadTabCounts, toast, onClose]);

    /**
     * Get action success message
     */
    const getActionSuccessMessage = (action) => {
        switch (action) {
            case 'take': return 'Issue assigned successfully';
            case 'fix': return 'Issue marked as fixed';
            case 'notIssue': return 'Issue marked as not an issue';
            case 'delete': return 'Issue deleted successfully';
            default: return 'Action completed successfully';
        }
    };

    /**
     * Setup auto-refresh with manual control
     */
    const setupAutoRefresh = useCallback(() => {
        if (refreshInterval.current) {
            clearInterval(refreshInterval.current);
        }
        
        if (autoRefreshEnabled) {
            refreshInterval.current = setInterval(() => {
                loadIssues(activeTab, true); // Auto-refresh
                loadTabCounts();
            }, 30000); // 30 seconds
        }
    }, [activeTab, autoRefreshEnabled, loadIssues, loadTabCounts]);

    /**
     * Manual refresh
     */
    const handleManualRefresh = useCallback(() => {
        loadIssues();
        loadTabCounts();
        toast({
            title: 'Refreshed',
            description: 'Data refreshed successfully',
            status: 'info',
            duration: 2000,
            isClosable: true
        });
    }, [loadIssues, loadTabCounts, toast]);

    /**
     * Handle URL parameters from Analytics navigation with proper timing
     */
    // useEffect(() => {
    //     if (urlParamsProcessed) return;

    //     const urlParams = new URLSearchParams(window.location.search);
    //     const clientId = urlParams.get('clientId');
    //     const clientName = urlParams.get('clientName');
    //     const tab = urlParams.get('tab');
        
    //     let hasUrlParams = false;

    //     // Apply client filter if provided
    //     if (clientId) {
    //         setUserFilters(prev => ({ ...prev, client_id: clientId }));
    //         hasUrlParams = true;
    //     }
        
    //     // Switch to specific tab if provided
    //     if (tab) {
    //         const tabIndex = parseInt(tab);
    //         if (tabIndex >= 0 && tabIndex < tabs.length) {
    //             setActiveTab(tabIndex);
    //             hasUrlParams = true;
    //         }
    //     }

    //     setUrlParamsProcessed(true);
    // }, [urlParamsProcessed]);

//vishnu--modified
    useEffect(() => {
        if (urlParamsProcessed) return;
        
        const clientId = searchParams.get('clientId');
        const clientName = searchParams.get('clientName');
        const tab = searchParams.get('tab');
        
        let hasUrlParams = false;
        
        // Apply client filter if provided
        if (clientId) {
            setUserFilters(prev => ({ ...prev, client_id: clientId }));
            hasUrlParams = true;
        }
        
        // Switch to specific tab if provided
        if (tab) {
            const tabIndex = parseInt(tab);
            if (tabIndex >= 0 && tabIndex < tabs.length) {
                setActiveTab(tabIndex);
                hasUrlParams = true;
            }
        }
        
        setUrlParamsProcessed(true);
    }, [urlParamsProcessed]);
    /**
     * Initialize component
     */
    useEffect(() => {
        if (!authServiceReady) return;
        
        loadFilterOptions();
        
        // Only load data after URL parameters are processed
        if (urlParamsProcessed) {
            loadIssues();
            loadTabCounts();
        }

        return () => {
            if (refreshInterval.current) {
                clearInterval(refreshInterval.current);
            }
        };
    }, [authServiceReady, urlParamsProcessed, loadFilterOptions, loadIssues, loadTabCounts]);

    /**
     * Handle tab change effect
     */
    useEffect(() => {
        if (authServiceReady && urlParamsProcessed) {
            loadIssues();
        }
    }, [activeTab, pagination.page, pagination.size, userFilters, authServiceReady, urlParamsProcessed, loadIssues]);

    /**
     * Handle filter change effect
     */
    useEffect(() => {
        if (authServiceReady && urlParamsProcessed) {
            loadTabCounts();
        }
    }, [userFilters, authServiceReady, urlParamsProcessed, loadTabCounts]);

    /**
     * Handle auto refresh toggle effect
     */
    useEffect(() => {
        setupAutoRefresh();
        
        return () => {
            if (refreshInterval.current) {
                clearInterval(refreshInterval.current);
            }
        };
    }, [setupAutoRefresh]);

    /**
     * Render action buttons for table rows
     */
    const renderActionButtons = useCallback((issue) => {
        const currentTab = tabs[activeTab];

        if (currentTab.key === 'issues') {
            return (
                <ButtonGroup size="sm" spacing={2}>
                    <Tooltip label="View Query" hasArrow>
                        <IconButton
                            icon={<MdCode />}
                            colorScheme="purple"
                            variant="ghost"
                            onClick={() => handleDataQueryClick(issue.dataQuery)}
                            aria-label="View Data Query"
                        />
                    </Tooltip>
                    <Tooltip label="Assign to me" hasArrow>
                        <IconButton
                            icon={<MdAssignment />}
                            colorScheme="blue"
                            variant="ghost"
                            onClick={() => handleAction(issue, 'take')}
                            aria-label="Assign to me"
                        />
                    </Tooltip>
                    {isAdmin && (
                        <Tooltip label="Delete Issue" hasArrow>
                            <IconButton
                                icon={<MdDelete />}
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => handleAction(issue, 'delete')}
                                aria-label="Delete Issue"
                            />
                        </Tooltip>
                    )}
                </ButtonGroup>
            );
        }

        if (currentTab.key === 'taken') {
            return (
                <ButtonGroup size="sm" spacing={2}>
                    <Tooltip label="View Query" hasArrow>
                        <IconButton
                            icon={<MdCode />}
                            colorScheme="purple"
                            variant="ghost"
                            onClick={() => handleDataQueryClick(issue.dataQuery)}
                            aria-label="View Data Query"
                        />
                    </Tooltip>
                    <Tooltip label="Mark as Fixed" hasArrow>
                        <IconButton
                            icon={<MdCheckCircle />}
                            colorScheme="green"
                            variant="ghost"
                            onClick={() => handleAction(issue, 'fix')}
                            aria-label="Mark as Fixed"
                        />
                    </Tooltip>
                    <Tooltip label="Mark as Not an Issue" hasArrow>
                        <IconButton
                            icon={<MdCancel />}
                            colorScheme="orange"
                            variant="ghost"
                            onClick={() => handleAction(issue, 'notIssue')}
                            aria-label="Mark as Not an Issue"
                        />
                    </Tooltip>
                    {isAdmin && (
                        <Tooltip label="Delete Issue" hasArrow>
                            <IconButton
                                icon={<MdDelete />}
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => handleAction(issue, 'delete')}
                                aria-label="Delete Issue"
                            />
                        </Tooltip>
                    )}
                </ButtonGroup>
            );
        }

        // For fixed and notIssue tabs, only show delete for admin (and only if actions column exists)
        if (isAdmin && currentTab.columns.includes('actions')) {
            return (
                <Tooltip label="Delete Issue" hasArrow>
                    <IconButton
                        icon={<MdDelete />}
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => handleAction(issue, 'delete')}
                        aria-label="Delete Issue"
                        size="sm"
                    />
                </Tooltip>
            );
        }

        return null;
    }, [activeTab, isAdmin, handleAction]);

    /**
     * Render table columns based on tab configuration (issue ID column removed)
     */
    const renderTableHeader = useCallback(() => {
        const currentTab = tabs[activeTab];
        const columnConfig = {
            client: { label: 'Client', width: '200px' },
            report: { label: 'Report', width: '250px' },
            message: { label: 'Message', width: '300px' },
            status: { label: 'Status', width: '120px' },
            issueTakenBy: { label: 'Assigned To', width: '150px' },
            fixedBy: { label: 'Fixed By', width: '150px' },
            notAnIssueBy: { label: 'Not Issue By', width: '150px' },
            created: { label: 'Created', width: '160px' },
            actions: { label: 'Actions', width: '120px' }
        };

        return (
            <Tr>
                {currentTab.columns.map(column => (
                    <Th key={column} width={columnConfig[column]?.width}>
                        {columnConfig[column]?.label}
                    </Th>
                ))}
            </Tr>
        );
    }, [activeTab]);

    /**
     * Render table row based on tab configuration (issue ID cell removed)
     */
    const renderTableRow = useCallback((issue) => {
        const currentTab = tabs[activeTab];
        
        const cellRenderers = {
            client: () => (
                <Text fontWeight="medium" noOfLines={1}>
                    {issue.clientName || 'Unknown Client'}
                </Text>
            ),
            report: () => (
                <Text noOfLines={2} maxW="240px">
                    {issue.reportDescription || 'Heartbeat'}
                </Text>
            ),
            message: () => (
                <Text 
                    noOfLines={3} 
                    maxW="290px" 
                    cursor="pointer"
                    color="blue.500"
                    _hover={{ textDecoration: 'underline' }}
                    onClick={() => handleMessageClick(issue.message)}
                >
                    {issue.message}
                </Text>
            ),
            status: () => (
                <Badge
                    colorScheme={
                        issue.resolutionStatus === 'Fixed' ? 'green' :
                        issue.resolutionStatus === 'Not an Issue' ? 'orange' :
                        issue.resolutionStatus === 'In Progress' ? 'blue' :
                        'gray'
                    }
                    variant="subtle"
                >
                    {issue.resolutionStatus || 'Open'}
                </Badge>
            ),
            issueTakenBy: () => (
                <Text fontSize="xs" noOfLines={1}>
                    {issue.issueTakenBy || '-'}
                </Text>
            ),
            fixedBy: () => (
                <Text fontSize="xs" noOfLines={1}>
                    {issue.fixedBy || '-'}
                </Text>
            ),
            notAnIssueBy: () => (
                <Text fontSize="xs" noOfLines={1}>
                    {issue.notAnIssueBy || '-'}
                </Text>
            ),
            created: () => (
                <Text fontSize="xs">
                    {formatDateTime(issue.createdAtUtc)}
                </Text>
            ),
            actions: () => renderActionButtons(issue)
        };

        return (
            <Tr key={issue.issueId} _hover={{ bg: hoverBg }}>
                {currentTab.columns.map(column => (
                    <Td key={column}>
                        {cellRenderers[column]()}
                    </Td>
                ))}
            </Tr>
        );
    }, [activeTab, handleMessageClick, renderActionButtons, hoverBg]);

    /**
     * Render loading state
     */
    if (loading && !issues.length) {
        return (
            <Flex justify="center" align="center" minH="400px">
                <VStack spacing={4}>
                    <Spinner size="xl" color="brand.500" thickness="4px" />
                    <Text color={textColor}>Loading issues...</Text>
                </VStack>
            </Flex>
        );
    }

    /**
     * Render error state
     */
    if (error && !issues.length) {
        return (
            <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                    {error}. Try refreshing manually.
                </AlertDescription>
            </Alert>
        );
    }

    /**
     * Main content component that can be rendered in normal or fullscreen mode
     */
    const MainContent = () => (
        <Box>
            <Grid templateColumns={showFilters ? { base: '1fr', lg: 'minmax(0, 1fr) 300px' } : '1fr'} gap={6}>
                {/* Left Column - Main Content */}
                <GridItem minW={0}>
                    <VStack spacing={6} align="stretch">
                        {/* ENHANCED: Page Header with new controls */}
                        <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                            <Box>
                                <Heading size="lg" color={headingColor} mb={2}>
                                    Issues Management
                                </Heading>
                                <Text color={textColor} fontSize="sm">
                                    Track and manage report triggered issues across all clients
                                </Text>
                            </Box>
                            
                            <HStack spacing={2} wrap="wrap">
                                {/* Auto Refresh Toggle */}
                                <FormControl display="flex" alignItems="center" width="auto">
                                    <FormLabel htmlFor="auto-refresh" mb="0" fontSize="sm" mr={2}>
                                        Auto Refresh
                                    </FormLabel>
                                    <Switch
                                        id="auto-refresh"
                                        isChecked={autoRefreshEnabled}
                                        onChange={toggleAutoRefresh}
                                        colorScheme="brand"
                                        size="sm"
                                    />
                                </FormControl>

                                {/* Manual Refresh Button */}
                                <Button
                                    leftIcon={<MdRefresh />}
                                    onClick={handleManualRefresh}
                                    variant="outline"
                                    size="sm"
                                >
                                    Refresh
                                </Button>

                                {/* Filter Toggle Button */}
                                <Tooltip label={showFilters ? "Hide Filters" : "Show Filters"} hasArrow>
                                    <IconButton
                                        icon={showFilters ? <MdVisibilityOff /> : <MdVisibility />}
                                        onClick={toggleFilters}
                                        variant="outline"
                                        size="sm"
                                        aria-label="Toggle Filters"
                                    />
                                </Tooltip>

                                {/* Fullscreen Toggle Button */}
                                <Tooltip label={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"} hasArrow>
                                    <IconButton
                                        icon={isFullscreen ? <MdFullscreenExit /> : <MdFullscreen />}
                                        onClick={toggleFullscreen}
                                        variant="outline"
                                        size="sm"
                                        aria-label="Toggle Fullscreen"
                                    />
                                </Tooltip>
                            </HStack>
                        </Flex>

                        {/* Tabs */}
                        <Tabs index={activeTab} onChange={handleTabChange} variant="enclosed">
                            <TabList>
                                {tabs.map((tab, index) => (
                                    <Tab key={tab.key}>
                                        <HStack spacing={2}>
                                            <Text>{tab.label}</Text>
                                            <Badge colorScheme="brand" variant="subtle">
                                                {formatNumber(tabCounts[index])}
                                            </Badge>
                                        </HStack>
                                    </Tab>
                                ))}
                            </TabList>

                            <TabPanels>
                                {tabs.map((tab, index) => (
                                    <TabPanel key={tab.key} px={0} py={0}>
                                        <Card bg={cardBg} borderColor={borderColor} mt={4}>
                                            <CardBody>
                                                {issues.length > 0 ? (
                                                    <VStack spacing={4} align="stretch">
                                                        {/* Table */}
                                                        <TableContainer>
                                                            <Table variant="simple" size="sm">
                                                                <Thead>
                                                                    {renderTableHeader()}
                                                                </Thead>
                                                                <Tbody>
                                                                    {issues.map(issue => renderTableRow(issue))}
                                                                </Tbody>
                                                            </Table>
                                                        </TableContainer>

                                                        {/* Pagination */}
                                                        <PaginationControls
                                                            currentPage={pagination.page}
                                                            totalPages={pagination.totalPages}
                                                            totalItems={pagination.total}
                                                            itemsPerPage={pagination.size}
                                                            onPageChange={handlePageChange}
                                                            onPageSizeChange={handlePageSizeChange}
                                                        />
                                                    </VStack>
                                                ) : (
                                                    <Flex justify="center" align="center" minH="200px">
                                                        <VStack spacing={4}>
                                                            <MdDescription size={48} color="gray" />
                                                            <Text color={textColor} textAlign="center">
                                                                No issues found for {tab.label.toLowerCase()}
                                                            </Text>
                                                        </VStack>
                                                    </Flex>
                                                )}
                                            </CardBody>
                                        </Card>
                                    </TabPanel>
                                ))}
                            </TabPanels>
                        </Tabs>
                    </VStack>
                </GridItem>

                {/* Right Column - Filters - Conditionally rendered */}
                {showFilters && (
                    <GridItem>
                        <Card bg={cardBg} borderColor={borderColor} position="sticky" top="104px" h="fit-content">
                            <CardHeader pb={2}>
                                <HStack justify="space-between">
                                    <HStack>
                                        <MdFilterList />
                                        <Text fontWeight="semibold">Filters</Text>
                                    </HStack>
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        onClick={handleClearFilters}
                                        leftIcon={<MdClose />}
                                    >
                                        Clear
                                    </Button>
                                </HStack>
                            </CardHeader>
                            <CardBody pt={0}>
                                <VStack spacing={4} align="stretch">
                                    <Box>
                                        <Text fontSize="sm" mb={2} color={textColor} fontWeight="medium">Client</Text>
                                        <Select
                                            placeholder="All Clients"
                                            value={userFilters.client_id}
                                            onChange={(e) => handleFilterChange('client_id', e.target.value)}
                                            size="sm"
                                        >
                                            {clients.map(client => (
                                                <option key={client.clientId} value={client.clientId}>
                                                    {client.clientName}
                                                </option>
                                            ))}
                                        </Select>
                                    </Box>
                                    
                                    <Box>
                                        <Text fontSize="sm" mb={2} color={textColor} fontWeight="medium">Report</Text>
                                        <Select
                                            placeholder="All Reports"
                                            value={userFilters.report_code}
                                            onChange={(e) => handleFilterChange('report_code', e.target.value)}
                                            size="sm"
                                        >
                                            {reports.map(report => (
                                                <option key={report.reportCode} value={report.reportCode}>
                                                    {report.reportDescription}
                                                </option>
                                            ))}
                                        </Select>
                                    </Box>

                                    <Box>
                                        <Text fontSize="sm" mb={2} color={textColor} fontWeight="medium">Created Date Range</Text>
                                        <style>{datePickerCustomStyles}</style>
                                        <DatePicker
                                            selectsRange={true}
                                            startDate={startDate}
                                            endDate={endDate}
                                            onChange={handleDateRangeChange}
                                            isClearable={true}
                                            placeholderText="All Dates"
                                            dateFormat="dd/MM/yyyy"
                                            className="custom-datepicker"
                                            maxDate={new Date()}
                                            showMonthDropdown
                                            showYearDropdown
                                            dropdownMode="select"
                                            calendarStartDay={1}
                                            wrapperClassName="custom-datepicker-wrapper"
                                        />
                                    </Box>
                                    
                                    {/* <Box>
                                        <Text fontSize="sm" mb={2} color={textColor} fontWeight="medium">Search</Text>
                                        <InputGroup size="sm">
                                            <InputLeftElement>
                                                <MdSearch color={textColor} />
                                            </InputLeftElement>
                                            <Input
                                                placeholder="Search issues..."
                                                value={userFilters.search}
                                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                            />
                                        </InputGroup>
                                    </Box> */}

                                    <Divider />

                                    {/* Current Tab Info */}
                                    <Box>
                                        <Text fontSize="sm" color={textColor} fontWeight="medium" mb={2}>
                                            Current Tab: {tabs[activeTab]?.label}
                                        </Text>
                                        <Text fontSize="xs" color={textColor}>
                                            {tabs[activeTab]?.key === 'issues' && 'Shows open issues not yet assigned'}
                                            {tabs[activeTab]?.key === 'taken' && 'Shows assigned open issues'}
                                            {tabs[activeTab]?.key === 'fixed' && 'Shows issues marked as fixed'}
                                            {tabs[activeTab]?.key === 'notIssue' && 'Shows issues marked as not issues'}
                                        </Text>
                                    </Box>

                                    {/* Applied Filters Summary */}
                                    <Box>
                                        <Text fontSize="sm" color={textColor} fontWeight="medium" mb={2}>
                                            Applied Filters
                                        </Text>
                                        <VStack spacing={1} align="start">
                                            {userFilters.client_id && (
                                                <Badge colorScheme="blue" variant="subtle" fontSize="xs" maxWidth="100%" whiteSpace="normal" textAlign="center" noOfLines={2}>
                                                    Client: {clients.find(c => c.clientId.toString() === userFilters.client_id)?.clientName || userFilters.client_id}
                                                </Badge>
                                            )}
                                            {userFilters.report_code && (
                                                <Badge colorScheme="green" variant="subtle" fontSize="xs" maxWidth="100%" whiteSpace="normal" textAlign="center" noOfLines={2}>
                                                    Report: {reports.find(r => r.reportCode.toString() === userFilters.report_code)?.reportDescription || userFilters.report_code}
                                                </Badge>
                                            )}
                                            {(userFilters.startDate && userFilters.endDate) && (
                                                <Badge colorScheme="purple" variant="subtle" fontSize="xs" maxWidth="100%" whiteSpace="normal" textAlign="center" noOfLines={2}>
                                                    Date: {startDate?.toLocaleDateString('en-GB')} - {endDate?.toLocaleDateString('en-GB')}
                                                </Badge>
                                            )}
                                            {userFilters.search && (
                                                <Badge colorScheme="pink" variant="subtle" fontSize="xs" maxWidth="100%" whiteSpace="normal" textAlign="center" noOfLines={2}>
                                                    Search: {userFilters.search}
                                                </Badge>
                                            )}
                                            {!userFilters.client_id && !userFilters.report_code && !userFilters.search && !userFilters.startDate && !userFilters.endDate && (
                                                <Text fontSize="xs" color={textColor}>
                                                    No user filters applied
                                                </Text>
                                            )}
                                        </VStack>
                                    </Box>

                                    {/* Filter Persistence Notice */}
                                    <Box bg={persistenceNoticeBg} p={3} borderRadius="md">
                                        <Text fontSize="xs" color={persistenceNoticeTextPrimary} fontWeight="medium" mb={1}>
                                            Filter Persistence
                                        </Text>
                                        <Text fontSize="xs" color={persistenceNoticeTextSecondary}>
                                            Client, Report, and Date Range filters remain active when switching tabs. Use "Clear" button to reset all filters.
                                        </Text>
                                    </Box>
                                </VStack>
                            </CardBody>
                        </Card>
                    </GridItem>
                )}
            </Grid>

            {/* Action Confirmation Modal */}
            <Modal isOpen={isOpen} onClose={onClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        Confirm Action
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text>
                            Are you sure you want to {' '}
                            {actionType === 'take' && 'take this issue'}
                            {actionType === 'fix' && 'mark this issue as fixed'}
                            {actionType === 'notIssue' && 'mark this issue as not an issue'}
                            {actionType === 'delete' && 'delete this issue'}
                            ?
                        </Text>
                        {selectedIssue && (
                            <Box mt={4} p={3} bg={hoverBg} borderRadius="md">
                                <Text fontSize="sm" fontWeight="medium">
                                    {selectedIssue.clientName} - {selectedIssue.reportDescription}
                                </Text>
                                <Text fontSize="xs" color={textColor} mt={1} noOfLines={3}>
                                    {selectedIssue.message}
                                </Text>
                            </Box>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" onClick={onClose} mr={3}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme={
                                actionType === 'delete' ? 'red' :
                                actionType === 'fix' ? 'green' :
                                actionType === 'notIssue' ? 'orange' :
                                'blue'
                            }
                            onClick={executeAction}
                            isLoading={actionLoading}
                        >
                            Confirm
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Message Display Modal */}
            <Modal isOpen={isMessageOpen} onClose={onMessageClose} size="lg" isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <HStack>
                            <MdMessage />
                            <Text>Issue Message</Text>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Box
                            p={4}
                            bg={hoverBg}
                            borderRadius="md"
                            fontFamily="mono"
                            fontSize="sm"
                            whiteSpace="pre-wrap"
                            overflowWrap="break-word"
                            maxH="400px"
                            overflowY="auto"
                        >
                            {messageToShow}
                        </Box>
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onMessageClose}>
                            Close
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={isDataQueryOpen} onClose={onDataQueryClose} size="xl" isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <HStack>
                            <MdCode />
                            <Text>Data Query</Text>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Box
                            p={4}
                            bg={hoverBg}
                            borderRadius="md"
                            fontFamily="mono"
                            fontSize="sm"
                            whiteSpace="pre-wrap"
                            overflowWrap="break-word"
                            maxH="500px"
                            overflowY="auto"
                            border="1px solid"
                            borderColor={borderColor}
                        >
                            {dataQueryToShow || 'No query available. Please Contact Admin'}
                        </Box>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            leftIcon={<MdContentCopy />}
                            colorScheme="blue"
                            mr={3}
                            onClick={handleCopyDataQuery}
                        >
                            Copy
                        </Button>
                        <Button onClick={onDataQueryClose}>
                            Close
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );

    // Render in fullscreen mode or normal mode
    if (isFullscreen) {
        return (
            <Box
                position="fixed"
                top={0}
                left={0}
                right={0}
                bottom={0}
                zIndex={9999}
                bg={fullscreenBg}
                p={6}
                overflowY="auto"
            >
                <MainContent />
            </Box>
        );
    }

    return <MainContent />;
}

export default Issues;